import React from 'react';
import { isNil, uniq } from 'lodash';

import Text from '~/components/core/TextComponents/Text';
import { CheckIcon } from '~/components/icons';
import colors from '~/theme/tailwind/colors';
import cn from '~/Utils/cn';

import ClampedText from '../../../ClampedText';
import LabelledText from '../../../LabelledText';
import RejectionReason from '../../../RejectionReason';
import { TEXTS } from '../../../texts';
import type { AskState, ConductorQuestion } from '../../../types';
import { askRejectionReason, ownWordsAnswer, questionOutcome } from '../../../utils';

const OPTIONS = 'Options';
/** The check on the option the handler chose. */
const CHOSEN = 'Chosen';

interface AnswerRecordProps {
  question: ConductorQuestion;
  state: AskState;
}

/**
 * A closed question, kept as the record of what was asked and what was decided: the option chosen is
 * checked where the selection sat and left at full strength, and the rest are faded. An answer in the
 * handler's own words sits beneath them under Your answer, even where the words match an option's,
 * and a rejection's note under Rejection reason, after the line naming who rejected it.
 */
const AnswerRecord: React.FC<AnswerRecordProps> = ({ question, state }) => {
  const options = uniq(question.options);
  const response = question.human_response;
  const ownWords = ownWordsAnswer(question);
  const chosenOption = response?.gesture === 'answered' && isNil(ownWords) ? response.answer : null;
  const outcome = questionOutcome(state, question);

  return (
    <>
      <ul aria-label={OPTIONS} className="m-0 flex list-none flex-col gap-8 p-0">
        {options.map((option) => {
          const isChosen = option === chosenOption;

          return (
            <li
              key={option}
              className={cn(
                'flex items-center gap-8 rounded-lg border border-solid p-12',
                isChosen ? 'border-blue-600' : 'border-transparent opacity-50'
              )}
            >
              {isChosen ? (
                <CheckIcon
                  role="img"
                  aria-label={CHOSEN}
                  size={16}
                  iconColor={colors.blue[600]}
                  className="shrink-0 rounded-full border border-solid border-blue-600 p-2"
                />
              ) : null}
              <Text variant={Text.VARIANTS.SM} weight={isChosen ? Text.WEIGHTS.SEMI_BOLD : Text.WEIGHTS.REGULAR}>
                {option}
              </Text>
            </li>
          );
        })}
      </ul>
      {ownWords ? (
        <LabelledText label={TEXTS.YOUR_ANSWER}>
          <ClampedText isPrimary>{ownWords}</ClampedText>
        </LabelledText>
      ) : null}
      {outcome ? (
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {outcome}
        </Text>
      ) : null}
      <RejectionReason reason={askRejectionReason(state, question)} />
    </>
  );
};

export default AnswerRecord;
